import React, { useEffect, useRef, useState } from 'react';
import './App.css';

// ── Ambient particle field (electric-blue / cyan, matching the Spin Storm logo)
const ParticleField: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let raf = 0;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = Math.max(window.innerHeight, 900); };
    resize();
    type P = { x: number; y: number; s: number; vx: number; vy: number; h: number; a: number; ph: number };
    const parts: P[] = Array.from({ length: 42 }, () => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      s: Math.random() * 2.4 + 0.6, vx: Math.random() * 0.6 - 0.3, vy: Math.random() * 0.6 - 0.3,
      h: 188 + Math.random() * 52, a: Math.random() * 0.4 + 0.2, ph: Math.random() * Math.PI * 2,
    }));
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of parts) {
        p.x += p.vx; p.y += p.vy; p.ph += 0.03;
        if (p.x < 0) p.x = canvas.width; if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height; if (p.y > canvas.height) p.y = 0;
        const a = p.a + Math.sin(p.ph) * 0.15;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.s, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.h},100%,65%,${a})`; ctx.fill();
        ctx.beginPath(); ctx.arc(p.x, p.y, p.s * 3, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(${p.h},100%,65%,${a * 0.18})`; ctx.stroke();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="particles" aria-hidden="true" />;
};

const VAULT = 'https://vault.eu.spin-storm.com';
const TICKETS_URL = 'https://b2b-tickets-156812959116.europe-west1.run.app';

type Game = { id: string; name: string; type: string; tag?: string; hue: [number, number] };
type CatalogueGame = { gameId: string; name: string };

const SHOWCASE_COUNT = 11;

// Hard content exclusions — brands we don't have distribution rights to. Hidden
// from the public site entirely. TWIN LIST: b2b-tickets/src/lib/exclusions.ts
// keeps the same set for the operator portal; keep the two in sync.
// 'high5' covers all High 5-branded titles (High 5 Casino, High 5 Heroes, …).
const EXCLUDED_BRANDS = ['betmgm', 'fanduel', 'luckybitsvegas', 'high5', 'caesarspalace'];
function isExcluded(name: string): boolean {
  const t = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  return EXCLUDED_BRANDS.some((b) => t.includes(b));
}

// Derive a display "type" label from the title's mechanic keywords.
function typeOf(name: string): string {
  const n = name.toLowerCase();
  if (/prize path/.test(n)) return 'Prize Path';
  if (/pop and win/.test(n)) return 'Pop & Win';
  if (/jackpot/.test(n)) return 'Jackpot';
  if (/x\s*1000|rush 1000|bankrush/.test(n)) return 'X 1000 Jackpot';
  if (/cluster|burst/.test(n)) return 'Cluster Pays';
  return 'Video Slot';
}

// The "theme" is the title with its mechanic/suffix words stripped, so the
// four Da Vinci variants (Collections, Prize Path, X 1000, Pop and Win) all
// collapse to one theme and we only ever promote the newest per theme.
function themeOf(name: string): string {
  const t = name
    .replace(/prize path|pop and win|bankrush 1000|rush 1000|x\s*1000|jackpot|collections|blitz|megaways|deluxe/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return (t || name).toLowerCase();
}

// Give each card a stable-ish hue pair derived from its id so the fallback
// gradient (shown until the real thumbnail paints) still looks varied.
function hueOf(id: string): [number, number] {
  const n = parseInt(id, 10) || 0;
  const base = 185 + ((n * 37) % 90); // stay in the cyan→violet band
  return [base, (base + 45) % 360];
}

// Curate the catalogue for the showcase: newest first (highest gameId),
// drop Slot Goddess, and keep only the newest title per theme so the grid
// stays varied instead of showing four Da Vincis / three Prize Paths.
function curate(all: CatalogueGame[]): Game[] {
  const num = (id: string) => parseInt(String(id).replace(/\D/g, ''), 10) || 0;
  const sorted = [...all].sort((a, b) => num(b.gameId) - num(a.gameId));
  const themes = new Set<string>();
  const picked: Game[] = [];
  for (const g of sorted) {
    if (!g.gameId || !g.name) continue;
    if (/slot goddess/i.test(g.name)) continue;
    if (isExcluded(g.name)) continue;
    const theme = themeOf(g.name);
    if (themes.has(theme)) continue;
    themes.add(theme);
    picked.push({ id: String(g.gameId), name: g.name, type: typeOf(g.name), hue: hueOf(String(g.gameId)) });
    if (picked.length >= SHOWCASE_COUNT) break;
  }
  if (picked.length) picked[0].tag = 'New';
  return picked;
}

// Fallback showcase used when the live catalogue can't be fetched (e.g. local
// `npm start`, which has no /catalogue.json proxy). Kept curated by the same
// rules so the site degrades gracefully.
const FALLBACK_GAMES: Game[] = curate([
  { gameId: '6006', name: 'Da Vinci Collections' },
  { gameId: '4440', name: 'Candy Burst' },
  { gameId: '4420', name: "Joker's Gems" },
  { gameId: '4390', name: 'Green Machine Blitz' },
  { gameId: '4367', name: "Billionaire's Bank Prize Path" },
  { gameId: '4366', name: "Dragon's Blessings Prize Path" },
  { gameId: '4360', name: 'Blazing Stampede' },
  { gameId: '4359', name: 'Zeus X 1000 Jackpot' },
  { gameId: '4356', name: 'Prosperity X 1000' },
  { gameId: '4320', name: 'Pop and Win' },
  { gameId: '4300', name: 'Dragon Kings' },
]);

const thumbOf = (g: Game) => `${VAULT}/html/Games/SCR${g.id}/thumbnail.jpg`;

// Build a full demo launch URL. Each launch gets a fresh random userId so two
// visitors never share a game session, and deviceType follows the visitor's
// device. Params match the game server's expected demo launch format.
function buildLaunchUrl(g: Game): string {
  // Params match the game server's exact launch format (captured from the RMG
  // lobby). Fresh numeric-suffixed userId per launch so demo sessions never
  // collide, and deviceType follows the visitor's device.
  const userId = 'tester' + (Math.floor(Math.random() * 90_000_000) + 10_000_000);
  const isMobile =
    typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const params = new URLSearchParams({
    debug: 'false',
    slamEnabled: 'on',
    slamWaitTime: '0',
    playMode: 'demo',
    currencyCode: 'USD',
    countryCode: 'ABW',
    languageCode: 'en',
    operatorCode: 'default',
    realitycheck_elapsed: '',
    realitycheck_limit: '',
    realitycheck_proceed: '',
    realitycheck_history: '',
    realitycheck_exit: '',
    deviceType: isMobile ? 'mobile' : 'desktop',
    engine: 'default',
    quality: 'HIGH',
    userId,
    clientToken: '',
    gameID: g.id,
  });
  return `${VAULT}/html/launch.html?${params.toString()}`;
}

const App: React.FC = () => {
  const [demo, setDemo] = useState<Game | null>(null);
  const [demoUrl, setDemoUrl] = useState('');
  const [navOpen, setNavOpen] = useState(false);
  const [games, setGames] = useState<Game[]>(FALLBACK_GAMES);
  const [dead, setDead] = useState<Record<string, true>>({});

  const openDemo = (g: Game) => { setDemoUrl(buildLaunchUrl(g)); setDemo(g); };

  // Pull the live catalogue (same-origin proxy → the vault) and self-curate,
  // so newly released games appear automatically with no redeploy. Falls back
  // to the baked-in list if the proxy isn't reachable.
  useEffect(() => {
    let alive = true;
    fetch('/catalogue.json', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data) => {
        const list: CatalogueGame[] = Array.isArray(data?.games) ? data.games : [];
        const curated = curate(list);
        if (alive && curated.length) setGames(curated);
      })
      .catch(() => { /* keep FALLBACK_GAMES */ });
    return () => { alive = false; };
  }, []);

  // Deep links like /#about don't scroll on a fresh load because the target
  // section isn't in the DOM yet when the browser processes the hash. After
  // mount, jump to the hashed section ourselves.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.replace('#', ''));
    if (!id) return;
    // Jump instantly (not smooth — smooth animates while async content/images
    // are still loading and lands in the wrong place), then re-correct a couple
    // of times as the catalogue and lazy thumbnails settle the layout.
    const jump = () => document.getElementById(id)?.scrollIntoView({ behavior: 'auto' });
    const timers = [200, 800, 1600].map((ms) => setTimeout(jump, ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  // A game with no thumbnail can't be showcased well — drop the whole card
  // (not just the <img>) so the grid never shows an empty tile.
  const markDead = (id: string) => setDead((d) => (d[id] ? d : { ...d, [id]: true }));
  const visibleGames = games.filter((g) => !dead[g.id]);

  useEffect(() => {
    document.body.style.overflow = demo ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDemo(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [demo]);

  const cover = (g: Game): React.CSSProperties => ({
    backgroundImage:
      `radial-gradient(120% 80% at 20% 0%, hsla(${g.hue[0]},95%,60%,0.55) 0%, transparent 55%),` +
      `radial-gradient(120% 90% at 100% 100%, hsla(${g.hue[1]},95%,55%,0.6) 0%, transparent 55%),` +
      `linear-gradient(135deg,#0a1120,#0f0b20)`,
  });

  return (
    <div className="app">
      <ParticleField />

      <header className="nav">
        <div className="nav-inner">
          <a className="brand" href="#top">
            <img src="/logo.png" alt="" className="brand-logo" />
            <span className="brand-name">Spin Storm</span>
          </a>
          <nav className={`nav-links ${navOpen ? 'open' : ''}`}>
            <a href="#games" onClick={() => setNavOpen(false)}>Games</a>
            <a href="#technology" onClick={() => setNavOpen(false)}>Technology</a>
            <a href="#operators" onClick={() => setNavOpen(false)}>Operators</a>
            <a href="#about" onClick={() => setNavOpen(false)}>About</a>
            <a className="nav-cta" href={TICKETS_URL}>Operator Login</a>
          </nav>
          <button className="nav-burger" aria-label="Menu" onClick={() => setNavOpen(v => !v)}>
            <span /><span /><span />
          </button>
        </div>
      </header>

      <main id="top">
        {/* Hero */}
        <section className="hero">
          <div className="hero-inner">
            <p className="eyebrow">Global Gaming Technology</p>
            <h1>The future of <span className="grad">slot gaming</span>, built for operators.</h1>
            <p className="lede">
              Spin Storm builds premium HTML5 slots and the platform to run them — certified,
              cross-platform, and ready to launch on your casino. Try the games yourself.
            </p>
            <div className="hero-cta">
              <a className="btn btn-primary" href="#games">Play demo games</a>
              <a className="btn btn-ghost" href="#operators">Become a partner</a>
            </div>
            <div className="stats">
              <div><strong>120+</strong><span>Games in studio</span></div>
              <div><strong>40+</strong><span>Operator partners</span></div>
              <div><strong>25ms</strong><span>Avg. load in-region</span></div>
              <div><strong>99.99%</strong><span>Platform uptime</span></div>
            </div>
          </div>
        </section>

        {/* Games showcase */}
        <section id="games" className="section">
          <div className="section-head">
            <h2>Play our games</h2>
            <p>Launch a live demo right in your browser — no download, no sign-up.</p>
          </div>
          <div className="games-grid">
            {visibleGames.map((g) => (
              <div className={`game-card ${g.tag ? 'featured' : ''}`} key={g.id}>
                <div className="game-cover" style={cover(g)}>
                  <span className="game-mark">SS</span>
                  <img
                    className="game-thumb"
                    src={thumbOf(g)}
                    alt={g.name}
                    loading="lazy"
                    onError={() => markDead(g.id)}
                  />
                  {g.tag && <span className="game-flag">{g.tag}</span>}
                  <button className="game-play" onClick={() => openDemo(g)} aria-label={`Play ${g.name} demo`}>
                    <span className="play-tri" /> Play demo
                  </button>
                </div>
                <div className="game-meta">
                  <h3>{g.name}</h3>
                  <span>{g.type}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Technology */}
        <section id="technology" className="section alt">
          <div className="section-head">
            <h2>Built to plug in</h2>
            <p>One integration, every device. Engineered for the realities of live casino operations.</p>
          </div>
          <div className="feature-grid">
            {[
              ['HTML5, everywhere', 'Every title runs natively on mobile web, apps, and desktop from a single build.'],
              ['Certified & fair', 'Independently tested RNG and jurisdiction-ready compliance out of the box.'],
              ['Remote Game Server', 'A single, resilient RGS integration exposes the full catalogue to your platform.'],
              ['Fast in-region', 'Edge-served assets keep spins snappy wherever your players are.'],
              ['Live configuration', 'Tune RTP ranges, limits, and promos per-operator without redeploys.'],
              ['Real-time insight', 'Player and revenue analytics, plus a support desk baked in.'],
            ].map(([t, d]) => (
              <div className="feature" key={t}>
                <div className="feature-dot" />
                <h3>{t}</h3>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Operators */}
        <section id="operators" className="section">
          <div className="operators">
            <div className="operators-copy">
              <h2>For operators &amp; aggregators</h2>
              <p>
                Get the full Spin Storm catalogue on your casino with one integration. Approved
                partners get a private vault to preview upcoming releases and manage support —
                using the same login as your Spin Storm support account.
              </p>
              <div className="hero-cta">
                <a className="btn btn-primary" href={TICKETS_URL}>Operator login</a>
                <a className="btn btn-ghost" href={`${TICKETS_URL}/login`}>Request access</a>
              </div>
            </div>
            <div className="operators-panel">
              <div className="panel-row"><span>Single RGS integration</span><span className="ok">✓</span></div>
              <div className="panel-row"><span>Demo &amp; certified game builds</span><span className="ok">✓</span></div>
              <div className="panel-row"><span>Private release vault</span><span className="ok">✓</span></div>
              <div className="panel-row"><span>Support ticketing included</span><span className="ok">✓</span></div>
              <div className="panel-row"><span>Teams &amp; email notifications</span><span className="ok">✓</span></div>
            </div>
          </div>
        </section>

        {/* About */}
        <section id="about" className="section alt">
          <div className="operators">
            <div className="operators-copy">
              <h2>About Spin Storm</h2>
              <p>
                Spin Storm Studio is a technology-first gaming company headquartered in
                Nicosia, Cyprus. We develop, license, and distribute premium slot game
                content to casino operators across the globe.
              </p>
            </div>
            <div className="operators-panel">
              <div className="panel-row"><span>Company</span><span className="ok" style={{ color: 'var(--ink)' }}>Spin Storm Ltd</span></div>
              <div className="panel-row"><span>Headquarters</span><span className="ok" style={{ color: 'var(--ink)' }}>Nicosia, Cyprus</span></div>
              <div className="panel-row"><span>Focus</span><span className="ok" style={{ color: 'var(--ink)' }}>B2B slot content &amp; platform</span></div>
              <div className="panel-row"><span>Compliance</span><span className="ok" style={{ color: 'var(--ink)' }}>18+ · Play responsibly</span></div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/logo.png" alt="" />
            <div>
              <span>Spin Storm</span>
              <p className="footer-tag">The world&apos;s most advanced slot gaming technology platform — powering next-generation casino experiences across the globe.</p>
            </div>
          </div>
          <div className="footer-cols">
            <div><h4>Platform</h4><a href="#games">Game Library</a><a href="#games">Demos</a><a href="#technology">Technology</a></div>
            <div><h4>Company</h4><a href="#about">About Us</a><a href="#operators">Operators</a><a href={`${TICKETS_URL}/login`}>Contact</a></div>
            <div><h4>Operators</h4><a href={TICKETS_URL}>Operator Login</a><a href={`${TICKETS_URL}/portal/vault`}>Game Vault</a></div>
            <div><h4>Legal</h4><a href="/legal/privacy.html">Privacy Policy</a><a href="/legal/terms.html">Terms of Use</a><a href="/legal/responsible-gaming.html">Responsible Gaming</a><a href="/legal/cookies.html">Cookie Policy</a></div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Spin Storm Ltd. All rights reserved. Registered &amp; operating from Nicosia, Cyprus.
            &nbsp;·&nbsp; 18+ | Play Responsibly | Spin Storm Studio™
          </p>
        </div>
      </footer>

      {/* Demo launcher */}
      {demo && (
        <div className="demo-overlay" onClick={() => setDemo(null)}>
          <div className="demo-modal" onClick={(e) => e.stopPropagation()}>
            <div className="demo-bar">
              <span><strong>{demo.name}</strong> · Demo</span>
              <div className="demo-actions">
                <a className="demo-newtab" href={demoUrl} target="_blank" rel="noreferrer">Open in new tab ↗</a>
                <button className="demo-close" onClick={() => setDemo(null)} aria-label="Close">✕</button>
              </div>
            </div>
            <div className="demo-stage" style={cover(demo)}>
              <div className="demo-placeholder">
                <div className="demo-spinner" />
                <p>Loading {demo.name}…</p>
              </div>
              <iframe title={`${demo.name} demo`} src={demoUrl} allow="autoplay; fullscreen" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
